// @ts-strict-ignore
import { LMFormatterService } from './lm-formatter.service';
import { LMListCardService } from '@list/services/lm-list-card.service';
import { NavigationResource } from '@resources/navigation.resource';
import { LMUrlUtilService } from '@services/utils';
import { lmProcessIndicatorFormatter } from './lm-process-indicator.formatter';
import { LMSimpleProgressService } from "@field/simpleprogress/lm-simple-progress.service";

import { escape } from 'lodash-es';

interface InternalNavigationParams {
  navigationParams: LMCardNavigationParams,
  displayValue: string,
  fieldStyle: LMFieldStyle,
  fieldId: string
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace lmCardViewFormatter {
  export const format = (
    lmFormatterService: LMFormatterService,
    lmListCardService: LMListCardService,
    lmUrlUtilService: LMUrlUtilService,
    lmSimpleProgressService: LMSimpleProgressService,
    row: number,
    cell: number,
    value: any,
    columnDef: ListGridColumn,
    dataView: LMListDataView,
    dataGrid: SohoDataGridStatic,
    /**
     * containerClass - The class placed on the surrounding div of the card.
     * This is useful for different margins/ padding depending on the context it's used in.
     */
    containerClass: string,
    automationId: string
  ): string => {
    if (dataView.isTreeMoreLink) {
      return lmFormatterService.getMoreLink(cell, dataView);
    } else if (lmFormatterService.isTotalRow(dataView)) {
      return lmFormatterService.formatTotalRowColumn(row, cell, value, columnDef, dataView, dataGrid);
    } else {
      const card = getCardTemplateData(lmListCardService, columnDef.displayField, dataView, automationId);

      if (card === undefined) {
        return '';
      }

      /**
       * Note:
       * This template should match the markup and CSS used in lm-list-card.component.html
       */
      const cardViewTemplate = `
        <div class="lm-card-view" data-automation-id="${card.automationId}">

          ${card?.image?.imageUrl ? getImageHTML(card, lmUrlUtilService) : ''}
          ${!card?.image?.imageUrl && card?.image?.isMissingImage && !card?.image?.imageForegroundText ? getMissingImageHTMLAsSVG(card) : ''}
          ${card?.image?.isMissingImage && card?.image?.imageForegroundText ? getAvatarHTML(card, lmUrlUtilService) : ''}

          <div class="lm-card-content">
            <div class="lm-card-flex">
              ${card.columns.map( (col, colIndex) => `
                  <div class="lm-column lm-align-${col.align}" style="${getColumnStyles(col)}">
                    <ul>
                      ${col.column.map((item, index) => `
                          <li>
                            <div class="lm-card-row ${!item.displayAsTag && item.isExternalNavigation || !item.displayAsTag && item.isInternalNavigation ? 'lm-hyperlink-container' : ''}">
                              ${item.label ? `<label class="lm-card-label ${lmFormatterService.getFieldStyleClasses(item.textStyle).join(' ')}">${item.label}:</label>` : ''}
                              ${item.alertCondition ? getAlertHTML(item, dataView, lmFormatterService, lmUrlUtilService) : ''}
                              
                               <!-- put HTML for field inside of "alert" label if alert -->
                              ${item.displayAsTag || isField(item) && !item.displayAsRating && !item.alertCondition ? `${getFieldValue(item, dataView, lmFormatterService, lmUrlUtilService)}` : ''}
                              ${item.isProcessIndicator ? `${getProcessIndicatorHTML(item)}` : ''}
                              ${item.progressFieldData ? `${getProgressBarHTML(item, lmSimpleProgressService)}` : '' }
                              ${item.displayAsRating ? `${getRatingHTML(item)}` : ''}
                              ${!item.displayAsTag && isExternalNavigation(item) ? getExternalNavigationHTML(item) : ''}
                              
                              ${!item.displayAsTag && isInternalNavigation(item) ? getInternalNavigationHTML(
                                {
                                  navigationParams: item.navigationParams,
                                  displayValue: item.displayValue,
                                  fieldStyle: item.fieldStyle,
                                  fieldId: item.fieldId
                                },
                                dataView, 
                                lmFormatterService, 
                                lmUrlUtilService
                              ) : ''}
                              
                              ${item.isFormButton ? getFormButtonHTML(item.cardButton) : ''}
                            </div>
                          </li>`).join('')}
                    </ul>
                  </div>`).join('')}
            </div>
          </div>
        </div>

        <!-- button  row -->
        ${card?.buttonRow?.length > 0 ? getButtonRowHTML(card, lmUrlUtilService, lmListCardService) : ''}`;

      let html = '<div class="' + containerClass + '">' + cardViewTemplate + '</div>';
      if (dataView?.doesNotExist) {
        html = '<del>' + html + '</del>';
      }

      return html;
    }
  };

  export const getAvatarHTML = (card: LMCardTemplateData, lmUrlUtilService: LMUrlUtilService): string => {
    if (card?.image?.navigationParams && !card?.image?.largeImage) {
      const href = buildUrlNavigation(card.image.navigationParams, lmUrlUtilService);
      return `
        <div class="${card?.image?.cardImageClasses}" data-automation-id="${card?.automationId}-image-container">
            <a href="${href}" data-automation-id='${card?.automationId}-image'>
              <span class="${card?.image?.bgColorClass}">${card?.image?.imageForegroundText}</span>
            </a>
        </div>
      `;
    }

    if (card?.image?.href) {
      return `
        <div class="${card?.image?.cardImageClasses}" data-automation-id="${card?.automationId}-image-container">
            <a href="${card?.image?.href}" data-automation-id='${card?.automationId}-image' target="${card?.image?.openExternal ? '_blank' : '_self'}">
              <span class="${card?.image?.bgColorClass}">${card?.image?.imageForegroundText}</span>
            </a>
        </div>
      `;
    }

    if (card?.image?.largeImage) {
      return `
        <div class="${card?.image?.cardImageClasses}" data-large-image='${JSON.stringify(card?.image?.largeImage)}' data-automation-id="${card?.automationId}-image-container">
            <a href="" data-automation-id='${card?.automationId}-image'>
              <span class="${card?.image?.bgColorClass}">${card?.image?.imageForegroundText}</span>
            </a>
        </div>
      `;
    }

    return `
      <div class="${card?.image?.cardImageClasses}" data-automation-id="${card?.automationId}-image-container">
        <span class="${card?.image?.bgColorClass}">${card?.image?.imageForegroundText}</span>
      </div>`;
  };

  export const getImageHTML = (card: LMCardTemplateData, lmUrlUtilService: LMUrlUtilService): string => {
    const altText = card?.image?.imageForegroundText || '';
    if (card?.image?.navigationParams && !card?.image?.largeImage) {
      const href = buildUrlNavigation(card.image.navigationParams, lmUrlUtilService);
      return `
        <div class="${card?.image?.cardImageClasses}" data-automation-id="${card?.automationId}-image-container">
            <a href="${href}" data-automation-id='${card?.automationId}-image'>
                <img class="avatar large" data-field-id="${card?.image?.fieldId}" data-navigation-name="${card?.image?.navigationParams?.navigationName}" src="${card?.image?.imageUrl}" alt="${altText}"/>
            </a>
        </div>
      `;
    }

    if (card?.image?.href) {
      return `
        <div class="${card?.image?.cardImageClasses}" data-automation-id="${card?.automationId}-image-container">
            <a href="${card?.image?.href}" data-automation-id='${card?.automationId}-image' target="${card?.image?.openExternal ? '_blank' : '_self'}">
                <img class="avatar large" src="${card?.image?.imageUrl}" alt="${altText}"/>
            </a>
        </div>
      `;
    }

    if (card?.image?.largeImage) {
      return `
        <div class="${card?.image?.cardImageClasses}" data-large-image='${JSON.stringify(card?.image?.largeImage)}' data-automation-id="${card?.automationId}-image-container">
            <a href="" data-automation-id='${card?.automationId}-image'>
                <img class="avatar large" src="${card?.image?.imageUrl}" alt="${altText}"/>
            </a>
        </div>
      `;
    }

    return `<div class="${card?.image?.cardImageClasses}" data-automation-id="${card?.automationId}-image-container"><img class="avatar large" src="${card?.image?.imageUrl}" alt="${altText}"/></div>`;
  };

  export const getMissingImageHTMLAsSVG = (card: LMCardTemplateData): string => `
    <div class="${card?.image?.cardImageClasses}">
      <svg class="icon avatar" focusable="false" aria-hidden="true" role="presentation">
        <use xlink:href="#icon-user-avatar"></use>
      </svg>
    </div>`;

  export const getInternalNavigationHTML = (
    internalNavigationParams: InternalNavigationParams,
    dataView: LMDataView,
    lmFormatterService: LMFormatterService,
    lmUrlUtilService: LMUrlUtilService
  ): string => {
    const isValid = lmFormatterService.isNavigationNameValid(internalNavigationParams.navigationParams.navigationName, dataView);
    if (isValid) {
      const markup = `
        <a href="${buildUrlNavigation(internalNavigationParams.navigationParams, lmUrlUtilService)}"
            class="hyperlink lm-card-internal-link ${lmFormatterService.getFieldStyleClasses(internalNavigationParams.fieldStyle).join(' ')}"
            data-navigation-name='${internalNavigationParams.navigationParams.navigationName}'
            data-automation-id='${internalNavigationParams?.fieldId}'
            data-field-id='${internalNavigationParams?.fieldId}'
        >${internalNavigationParams.displayValue}</a>`;

      return markup;

    } else {
      return `<span>${internalNavigationParams.displayValue}</span>`;
    }
  };

  export const buildUrlNavigation = (navigationParams: LMCardNavigationParams, lmUrlUtilService: LMUrlUtilService): string => {
    const navigationResource = new NavigationResource({ resourceId: navigationParams.dataView.resourceId, actionName: navigationParams.navigationName });
    return lmUrlUtilService.buildUrl({ resource: navigationResource });
  };

  export const getExternalNavigationHTML = (item) => `<a href="${item.httpUrl}" class="hyperlink">${item.displayValue}</a>`;

  export const getAlertHTML = (
    item: LMListViewTemplateField,
    dataView: LMDataView,
    lmFormatterService: LMFormatterService,
    lmUrlUtilService: LMUrlUtilService
    ): string => `
        ${item?.alertCondition?.icon && item?.alertCondition?.iconUrl === undefined ? `
          <label class="lm-card-field lm-card-alert" title="${item.alertCondition.alertTooltip}">
          <svg class="icon datagrid-alert-icon icon-${item.alertCondition.icon}">
            <use xlink:href="#icon-${item.alertCondition.icon}"</use>
          </svg>
          ${item.displayAsTag || isField(item) && !item.displayAsRating  ? `${getFieldValue(item, dataView, lmFormatterService, lmUrlUtilService)}` : ''}
        </label>
        ` : '' }
        ${item?.alertCondition?.iconUrl && item?.alertCondition?.icon === undefined ? `
          <label class="lm-card-field lm-card-alert" title="${item.alertCondition.alertTooltip}">
            <img src="${item.alertCondition.iconUrl}"/>
            ${item.displayAsTag || isField(item) && !item.displayAsRating  ? `${getFieldValue(item, dataView, lmFormatterService, lmUrlUtilService)}` : ''}
          </label>
        ` : '' }
      `;

  export const getButtonRowHTML = (card: LMCardTemplateData, lmUrlUtilService: LMUrlUtilService, lmListCardService: LMListCardService): string => `
      <div class="lm-card-button-row lm-align-${card.buttonRowAlignment}">
          ${card.buttonRow.map(button => `
                ${button.isDisplayAsLink && !button.isIconOnly ? getButtonAsLink(button, lmUrlUtilService) : ''}
                ${button.isIconOnly ? getButtonAsIcon(lmListCardService.encodeHtmlSingleQuote(button)) : '' }
                ${!button.isIconOnly && !button.isDisplayAsLink ? getDefaultButton(lmListCardService.encodeHtmlSingleQuote(button)) : '' }
             `).join('')}
      </div>`;

  // Todo: Button menu not supported yet in datagrid
  export const getButtonMenuHTML = (card: LMCardTemplateData, lmUrlUtilService: LMUrlUtilService, lmListCardService: LMListCardService): string => `
      <div class="lm-card-button-menu-container">
      <button class="btn-actions lm-datagrid-card-more-button" type="button">
        <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
          <use href="#icon-more"></use>
        </svg>
        <span class="audible" data-translate="text">More</span>
      </button>

      <div class="popupmenu-wrapper placeable bottom">
        <ul id="button-menu" class="popupmenu has-icons is-open" role="listbox" aria-labelledby="undefined" aria-label="List" aria-hidden="false">
          ${card.buttonMenu.map((button, index) => `
            <li role="option" aria-posinset="${index}" aria-setsize="0">
                ${button.isDisplayAsLink && !button.isIconOnly ? getButtonAsLink(button, lmUrlUtilService) : `
                  <a href="" data-displayfield='${JSON.stringify(button.actionReference)}'>
                    <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
                      <use xlink:href="#icon-${button.icon}"></use>
                    </svg>
                    <span>${button.actionReference.label}</span>
                  </a>
                `}
            </li>
          `).join('')}
        </ul>
      </div>
    </div>
    `;

  export const getFormButtonHTML = (button: LMCardViewButton): string => {
    const flyoutTooltip = button.actionReference?.cardViewFormButtonSpec?.openInOverlay ? Soho.Locale.translate('FlyoutOpenFullScreen') : '';
    if (button.isDisplayAsLink) {
      return `<a class="lm-form-button lm-card-button hyperlink" data-displayfield='${JSON.stringify(button.actionReference)}'
                ${flyoutTooltip ? `title="${flyoutTooltip}"` : ''}>
                ${button.icon ? `
                <svg class="icon text-link" focusable="false" aria-hidden="true" role="presentation">
                  <use xlink:href="#icon-${button.icon}"></use>
                </svg>
              ` : ''}
              ${button.actionReference.label}
              </a>`;
    } else {
      return `<button class="lm-form-button lm-card-button hyperlink" data-displayfield='${JSON.stringify(button.actionReference)}'
                ${flyoutTooltip ? `title="${flyoutTooltip}"` : ''}>
                ${button.icon ? `
                  <svg class="icon text-link" focusable="false" aria-hidden="true" role="presentation">
                    <use xlink:href="#icon-${button.icon}"></use>
                  </svg>
                ` : ''}
                ${button.actionReference.label}
              </button>`;
    }
  };

  /** Note,
   * do not remove lm-card-button class, see onColumnClicked() in lm-datagrid-base.component
   * extra span here to match icon button used in lm-list-card.component.html
   * **/
  export const getButtonAsIcon = (button: LMCardViewButton): string => {
    const flyoutTooltip = button.actionReference?.cardViewFormButtonSpec?.openInOverlay ? Soho.Locale.translate('FlyoutOpenFullScreen') : '';
    const tooltip = flyoutTooltip || button.actionReference.tooltip || button.actionReference.label;
    return `
    <button
      ${button.icon ? `class="btn-icon lm-icon-button lm-card-button"` : `class="btn-icon lm-icon-button lm-card-button lm-missing-soho-icon"`}
      title='${tooltip}'
      data-automation-id="${button.automationId}"
      data-displayfield='${JSON.stringify(button.actionReference)}'
    >
      ${button.icon ? `<span>${button.actionReference.label}</span>` : ``}
      ${button.icon ? `
      <svg class="icon lm-icon" focusable="false" aria-hidden="true" role="presentation">
        <use xlink:href="#icon-${button.icon}"></use>
      </svg>` : `<span><span style="background-image: url('${button.iconUrl}')">${button.actionReference.label ? button.actionReference.label : `&nbsp;`}</span></span>`}
    </button>`;
  };

  export const getButtonAsLink = (button: LMCardViewButton, lmUrlUtilService: LMUrlUtilService): string => {
    /** Note, do not remove lm-card-button class, see onColumnClicked() in lm-datagrid-base.component **/
    let href = 'javascript:void(0);';
    let targetBlank = false;

    if (button.httpUrl && button.isOpenExternal && (button.actionReference.cardViewFormButtonSpec
      && button.actionReference.cardViewFormButtonSpec.httpUrl
      && !button.actionReference.cardViewFormButtonSpec.httpUrl.variables)) {

      href = button.httpUrl;
      targetBlank = true;
    } else if (button.navigationParams && !button.isOpenExternal) {
      href = buildUrlNavigation(button.navigationParams, lmUrlUtilService);
    }

    const flyoutTooltip = button.actionReference?.cardViewFormButtonSpec?.openInOverlay ? Soho.Locale.translate('FlyoutOpenFullScreen') : '';

    // The issue on single quote does not affect buttons displayed as link
    return `<a href="${href}" ${targetBlank ? 'target="_blank"' : ''} class="hyperlink lm-card-button "
      data-automation-id="${button.automationId}"
      ${flyoutTooltip ? `title="${flyoutTooltip}"` : ''}
      data-displayfield='${JSON.stringify(button.actionReference)}'>${button.actionReference.label}</a>`;
  };

  export const getDefaultButton = (button: LMCardViewButton): string => {
    const classArray = [
      'btn',
      'lm-card-button'
    ];

    if (button.isPrimary) {
      classArray.push('lm-is-primary');
    } else if (button.fieldStyle && button.fieldStyle.color) {
      classArray.push('lpl-' + button.fieldStyle.color);
    }

    const flyoutTooltip = button.actionReference?.cardViewFormButtonSpec?.openInOverlay ? Soho.Locale.translate('FlyoutOpenFullScreen') : '';

    /** Note, do not remove lm-card-button class, see onColumnClicked() in lm-datagrid-base.component **/
    return `<button class='${classArray.join(' ')}'
      data-automation-id="${button.automationId}"
      ${flyoutTooltip ? `title="${flyoutTooltip}"` : ''}
      data-displayfield='${JSON.stringify(button.actionReference)}'
      >${button.actionReference.label}</button>`;
  };

  export const getCardTemplateData = (
    lmListCardService: LMListCardService,
    displayField: LMListDisplayField,
    dataView: LMListDataView,
    automationId: string
  ): LMCardTemplateData => lmListCardService.getCardTemplateData(displayField, dataView, automationId);

  export const isInternalNavigation = (item: LMListViewTemplateField): boolean => {
    if (!item || item.value === '' || !item.navigationParams) {
      return false;
    }

    return true;
  };

  export const isExternalNavigation = (item: LMListViewTemplateField): boolean => {
    if (!item || !item.httpUrl || item.httpUrl.trim().length === 0) {
      return false;
    }

    return true;
  };

  export const isField = (item: LMListViewTemplateField): boolean => {
    if (item && (item.isField || item.displayAsTag)) {
      return true;
    }

    return false;
  };

  export const getProcessIndicatorHTML = (item: LMListViewTemplateField): string => lmProcessIndicatorFormatter.getProcessIndicatorHTML(item.processIndicatorDisplayData);

  export const getFieldValue = (
    item: LMListViewTemplateField,
    dataView: LMDataView,
    lmFormatterService: LMFormatterService,
    lmUrlUtilService: LMUrlUtilService
  ): string => {
    const styles: string[] = [];

    if (item.textStyle && item.isFormText) {
      lmFormatterService.getFieldStyleClasses(item.textStyle).forEach((style) => styles.push(style));
    }

    if (item.fieldStyle) {
      lmFormatterService.getFieldStyleClasses(item.fieldStyle).forEach((style) => styles.push(style));
    }

    if (item.displayAsTag) {
      const classArray = [ 'tag' ];

      if (item.fieldStyle && item.fieldStyle.color) {
        classArray.push('lpl-' + item.fieldStyle.color + '-bg'); // for tag background
        classArray.push('lpl-white');  // for tag foreground text when there is background color
      }

      if (isExternalNavigation(item)) {
        const externalLinkClasses = classArray.join(' ');
        return `
          <div class="lm-card-tag-container">
            <a href="${item.httpUrl}" class="lm-card-field is-clickable ${externalLinkClasses}">
              <span>${item.displayValue}</span>
              <span>
                <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
                  <use xlink:href="#icon-caret-right"></use>
                </svg>
                <span class="audible"></span>
              </span>
            </a>
          </div>
        `;
      }

      if (isInternalNavigation(item)) {
        const isValid = lmFormatterService.isNavigationNameValid(item.navigationParams.navigationName, dataView);
        if (isValid) {
          const internalLinkClasses = classArray.join(' ');
          return `
            <div class="lm-card-tag-container">
              <a href="${buildUrlNavigation(item.navigationParams, lmUrlUtilService)}"
                  class="lm-card-field is-clickable ${styles.join(' ')} ${internalLinkClasses}"
                  data-displayfield='${JSON.stringify(item)}'
                  data-navigation-params='${JSON.stringify(item.navigationParams)}'
              >
                <span>${item.displayValue}</span>
                <span>
                  <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
                    <use xlink:href="#icon-caret-right"></use>
                  </svg>
                  <span class="audible"></span>
                </span>
              </a>
            </div>
          `;
        }
      }

      const classes = classArray.join(' ');
      return `<div class="lm-card-tag-container"><span class="lm-card-field ${classes}">${item.displayValue}</span></div>`;

    } else if(item.displayAsAlert) {
      return `<span class="lm-card-field ${styles.join(' ')} ${item.displayAsAlert.color}">
        <svg class="icon ${item.displayAsAlert.color}" focusable="false" aria-hidden="true" role="presentation">
          <use xlink:href="#icon-${item.displayAsAlert.icon}"></use>
        </svg>${item.displayValue}</span>`;
    } else {
      // DomSanitizer sanitize was used earlier in this case and was reverted. See LMCLIENT-26266
      // It converts the string to HTML decimal code like &#38 for '&'. It does not escape value to html entities &amp;
      // Using lodash escape the XSS attack instead to fix LMWEB-23302
      // Linkbacks as richtext are in the main column - LMWEB-23302
      const skipEscape = item.isRichText;
      const value = skipEscape ? item.displayValue : escape(item.displayValue);

      if (item.fieldStyle) {
        // value is inside of alert label when there is an alert condition.
        // should not have the lm-card-field class in this case
        if (item.lines) {
          return `<span class="${item?.alertCondition ? '' : 'lm-card-field'} ${styles.join(' ')}" style="-webkit-line-clamp: ${item.lines}" >${value}</span>`;
        }

        return `<span class="${item?.alertCondition ? '' : 'lm-card-field'} ${styles.join(' ')}">${value}</span>`;
      } else {
        if (item.lines) {
          return `<span class="${item?.alertCondition ? '' : 'lm-card-field'}" style="-webkit-line-clamp: ${item.lines}">${value}</span>`;
        }

        return `<span class="${item?.alertCondition ? '' : 'lm-card-field'}">${value}</span>`;
      }

    }
  };

  export const getProgressBarHTML = (item: LMListViewTemplateField, lmSimpleProgressService: LMSimpleProgressService): string => {
    const width = lmSimpleProgressService.getWidth(item.progressFieldData);
    const colorClass = lmSimpleProgressService.getColorClass(item.progressFieldData);
    const title = lmSimpleProgressService.getTitle(item.progressFieldData);
    const displayValue = lmSimpleProgressService.getDisplayValue(item.progressFieldData);
    const inverseClass = lmSimpleProgressService.getInverseClass(item.progressFieldData);

    return `
      <div class="lm-simple-progress" [attr.title]="${title}">
        ${width > 0 ? `<div class="${colorClass}" style="width: ${width}%;"></div>` : ''}
        <div class="lm-range-text ${inverseClass}">${displayValue}</div>
      </div>
    `;
  };

  export const getRatingHTML = (item: LMListViewTemplateField): string => `
      <div class="rating is-readonly">
        <fieldset>
          <legend class="audible">Rating</legend>  
          ${item?.ratingOptions?.map( (item, index) => `
            <input type="radio" class="${item.class}" name="${item.id}" id="${item.id}"/>
            <label for="${item.id}">
              <svg class="icon" focusable="false" aria-hidden="true" role="presentation">
                <use href="${item.href}"></use>
              </svg>
              <span class="audible">${item.label}</span>
            </label>
          `).join('')}  
        </fieldset>
      </div>
    `;

  export const getColumnStyles = (col: LMCardTemplateColumn): string => {
    if (col.percent) {
      return `width: ${ col.percent + '%' }; flex-grow: 0`;
    }

    return '';
  };
}
